import { Box, Popover, styled, TextField, Typography } from "@mui/material";
import useMemoizedFn from "ahooks/es/useMemoizedFn";
import { bindTrigger } from "material-ui-popup-state";
import { bindPopover, usePopupState } from "material-ui-popup-state/hooks";
import React, { useState } from "react";
import { DateRange, RangeKeyDict } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import {
  ArrowRight as ArrowRightIcon,
  Calendar as CalendarIcon,
} from "react-feather";
import { useTranslation } from "react-i18next";
import { AmtInputAdornment } from "../../../../../components";
import { useDateFns } from "../../../../../hooks";
import { usePermissions } from "../../../../permissions/hooks";
import { BookingDate } from "../../../domain";
import "./BookingDatePicker.css";

export interface BookingDatePickerProps {
  date: BookingDate;
  onChange: (newDate: BookingDate) => void;
}

export const BookingDatePicker: React.FC<BookingDatePickerProps> = React.memo(
  ({ date, onChange }) => {
    const { t } = useTranslation("booking");
    const { bookings } = usePermissions();
    const { format, locale } = useDateFns();
    const [focusedRange, setFocusedRange] = useState<
      [number, 0 | 1] | undefined
    >();
    const popupState = usePopupState({
      popupId: "book--filters-booking-date",
      variant: "popover",
    });
    const isDateDisabled = useMemoizedFn((date: Date) => {
      return !bookings.canViewBookingsOnDate(date);
    });

    const handleChange = useMemoizedFn((e: RangeKeyDict) => {
      const startDate = e.selection.startDate ?? new Date();
      const endDate = e.selection.endDate ?? startDate;
      const bookingDate = BookingDate.of(startDate, endDate);
      onChange(bookingDate);
      setFocusedRange(undefined);
    });

    const focusStartDate = useMemoizedFn(() => {
      setFocusedRange([0, 0]);
    });
    const focusEndDate = useMemoizedFn(() => {
      setFocusedRange([0, 1]);
    });

    return (
      <Box sx={{ display: "flex" }}>
        <AmtInputAdornment placement="start">
          <CalendarIcon size={16} />
        </AmtInputAdornment>
        <TextField
          fullWidth
          sx={{ flexGrow: 1 }}
          inputProps={{ readOnly: true }}
          className="MuiInput-leftAdornment MuiInput-rightAdornment"
          data-testid="book--filters--booking-date-start"
          aria-describedby="book--filters-booking-date-start"
          value={format(date.startDate, "dd.MM.yyyy.")}
          {...bindTrigger(popupState)}
          onFocus={focusStartDate}
        />
        <AmtInputAdornment>
          <ArrowRightIcon size={16} />
        </AmtInputAdornment>
        <TextField
          fullWidth
          sx={{ flexGrow: 1 }}
          className="MuiInput-leftAdornment"
          inputProps={{ readOnly: true }}
          data-testid="book--filters--booking-date-end"
          aria-describedby="book--filters-booking-date-end"
          value={format(date.endDate, "dd.MM.yyyy.")}
          {...bindTrigger(popupState)}
          onFocus={focusEndDate}
        />
        <StyledPopover
          {...bindPopover(popupState)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <Notice>
            <Typography variant="body2">
              {t("filter.datepicker-notice")}
            </Typography>
          </Notice>
          <DateRangeContainer>
            <DateRange
              disabledDay={isDateDisabled}
              displayMode="dateRange"
              onChange={handleChange}
              showPreview={true}
              showDateDisplay={false}
              rangeColors={["#00CC99"]}
              focusedRange={focusedRange}
              locale={locale}
              ranges={[
                {
                  startDate: date.startDate,
                  endDate: date.endDate,
                  key: "selection",
                },
              ]}
            />
          </DateRangeContainer>
        </StyledPopover>
      </Box>
    );
  }
);

const Notice = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
  backgroundColor: theme.palette.cyclamen.regular,
  color: "white",
  borderTopLeftRadius: 10,
  borderTopRightRadius: 10,
}));

const DateRangeContainer = styled(Box)(({ theme }) => ({
  border: "1px solid",
  borderColor: theme.palette.shades.greyLight,
  borderBottomLeftRadius: "10px",
  borderBottomRightRadius: "10px",
  overflow: "hidden",
}));

const StyledPopover = styled(Popover)(({ theme }) => ({
  ".MuiPaper-root": {
    overflow: "hidden",
    borderRadius: "10px",
    boxShadow: "0px 4px 8px rgba(20, 17, 16, 0.1)",
  },
}));
